import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Play, Smartphone } from 'lucide-react';
import emailjs from '@emailjs/browser';

const TEAL = '#00A878';
const DARK = '#0D1117';
const WHITE = '#FFFFFF';
const MUTED = '#6c757d';

const EMAILJS_SERVICE_ID = 'service_i8c9qzc';
const EMAILJS_CONTACT_TEMPLATE_ID = 'template_ftcgc88';
const EMAILJS_PUBLIC_KEY = 'fQM3oK6P6hlji2N7a';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_CONTACT_TEMPLATE_ID,
        {
          name: form.name,
          email: form.email,
          phone: form.phone || 'Not provided',
          subject: form.subject,
          message: form.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ background: WHITE }}>

      {/* ── HERO ── */}
      <div className="section-pad-lg" style={{ background: WHITE, padding: '80px 0 60px' }}>
        <div className="container">
          <div className="row align-items-start g-5 mb-5">
            <div className="col-lg-6">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  GET IN TOUCH
                </span>
              </div>
              <h1 style={{
                fontWeight: '900', fontSize: '2.8rem',
                lineHeight: 1.15, letterSpacing: '-1px',
                color: DARK, marginTop: '0',
              }}>
                We'd love to<br />
                <span style={{ color: '#adb5bd' }}>hear from you</span>
              </h1>
            </div>
            <div className="col-lg-4 offset-lg-2 d-flex align-items-end" style={{ paddingBottom: '8px' }}>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                Whether you have a question about parking spots, want to list your lot, or just want to say hello — our team is ready to help.
              </p>
            </div>
          </div>

          <div className="contact-hero-img" style={{ borderRadius: '16px', overflow: 'hidden', height: '380px' }}>
            <img
              src="/images/Contact-us.webp"
              alt="Contact Spot Finder team"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>

      {/* ── CONTACT DETAILS + FORM ── */}
      <div className="section-pad-lg" style={{ background: '#F5F5F5', padding: '100px 0' }}>
        <div className="container">
          <div className="row g-5">

            {/* LEFT — contact details */}
            <div className="col-lg-5">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  CONTACT DETAILS
                </span>
              </div>
              <h2 style={{
                fontWeight: '900', fontSize: '2.8rem',
                color: DARK, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: '16px',
              }}>
                Find us here
              </h2>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '40px' }}>
                Our team is available Monday to Friday, 8am to 6pm EAT. We usually respond within a few hours.
              </p>

              <div className="d-flex flex-column gap-3">
                {[
                  { icon: <Phone size={22} color={TEAL} />, label: 'Phone', value: '+254 706 389794', sub: 'Mon–Fri, 8am–6pm EAT' },
                  { icon: <Mail size={22} color={TEAL} />, label: 'Email', value: 'hello@spotfinder.co.ke', sub: 'We reply within a few hours' },
                  { icon: <MapPin size={22} color={TEAL} />, label: 'Office', value: 'Westlands, Nairobi', sub: 'Kenya' },
                  { icon: <Clock size={22} color={TEAL} />, label: 'Working Hours', value: 'Mon – Fri: 8am – 6pm', sub: 'Sat: 9am – 1pm' },
                ].map(({ icon, label, value, sub }) => (
                  <div key={label} style={{
                    background: WHITE, borderRadius: '14px',
                    padding: '20px 24px', display: 'flex',
                    alignItems: 'center', gap: '18px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f0',
                    transition: 'border-color 0.2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#f0f0f0'}>
                    <div style={{
                      background: '#E8F5F1', borderRadius: '10px',
                      width: '48px', height: '48px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#8B949E', letterSpacing: '1px', marginBottom: '4px' }}>
                        {label.toUpperCase()}
                      </div>
                      <div style={{ fontWeight: '700', color: DARK, fontSize: '0.95rem' }}>{value}</div>
                      <div style={{ color: MUTED, fontSize: '0.82rem', marginTop: '2px' }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — contact form */}
            <div className="col-lg-7">
              <div className="contact-form-wrap" style={{
                background: WHITE, borderRadius: '20px',
                padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0',
              }}>
                <div style={{
                  display: 'inline-block',
                  background: '#E8F5F1', border: '1px solid #B2DFDB',
                  borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
                }}>
                  <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                    SEND A MESSAGE
                  </span>
                </div>
                <h2 style={{
                  fontWeight: '900', fontSize: '2.8rem',
                  color: DARK, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: '32px',
                }}>
                  Let's talk
                </h2>

                {sent ? (
                  <div style={{
                    background: '#E8F5F1', border: '1px solid #B2DFDB',
                    borderRadius: '14px', padding: '40px', textAlign: 'center',
                  }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: WHITE, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 20px',
                      boxShadow: '0 4px 16px rgba(0,168,120,0.2)',
                    }}>
                      <Send size={28} color={TEAL} />
                    </div>
                    <h5 style={{ fontWeight: '800', color: DARK, marginBottom: '8px', fontSize: '1.2rem' }}>
                      Message sent!
                    </h5>
                    <p style={{ color: MUTED, margin: 0, fontSize: '0.95rem', lineHeight: 1.7 }}>
                      Thanks for reaching out. We'll get back to you within a few hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row g-3">

                      <div className="col-md-6">
                        <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                          Full Name *
                        </label>
                        <input type="text" name="name" required
                          value={form.name} onChange={handleChange}
                          placeholder="John Kamau"
                          style={{
                            width: '100%', padding: '12px 16px',
                            border: '1.5px solid #e9ecef', borderRadius: '10px',
                            fontSize: '14px', color: DARK, outline: 'none',
                            background: WHITE, transition: 'border-color 0.2s',
                          }}
                          onFocus={e => e.target.style.borderColor = TEAL}
                          onBlur={e => e.target.style.borderColor = '#e9ecef'} />
                      </div>

                      <div className="col-md-6">
                        <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                          Email Address *
                        </label>
                        <input type="email" name="email" required
                          value={form.email} onChange={handleChange}
                          placeholder="john@email.com"
                          style={{
                            width: '100%', padding: '12px 16px',
                            border: '1.5px solid #e9ecef', borderRadius: '10px',
                            fontSize: '14px', color: DARK, outline: 'none',
                            background: WHITE, transition: 'border-color 0.2s',
                          }}
                          onFocus={e => e.target.style.borderColor = TEAL}
                          onBlur={e => e.target.style.borderColor = '#e9ecef'} />
                      </div>

                      <div className="col-md-6">
                        <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                          Phone Number
                        </label>
                        <input type="tel" name="phone"
                          value={form.phone} onChange={handleChange}
                          placeholder="+254 700 000 000"
                          style={{
                            width: '100%', padding: '12px 16px',
                            border: '1.5px solid #e9ecef', borderRadius: '10px',
                            fontSize: '14px', color: DARK, outline: 'none',
                            background: WHITE, transition: 'border-color 0.2s',
                          }}
                          onFocus={e => e.target.style.borderColor = TEAL}
                          onBlur={e => e.target.style.borderColor = '#e9ecef'} />
                      </div>

                      <div className="col-md-6">
                        <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                          Subject *
                        </label>
                        <select name="subject" required
                          value={form.subject} onChange={handleChange}
                          style={{
                            width: '100%', padding: '12px 16px',
                            border: '1.5px solid #e9ecef', borderRadius: '10px',
                            fontSize: '14px', color: form.subject ? DARK : MUTED,
                            outline: 'none', background: WHITE,
                            transition: 'border-color 0.2s', cursor: 'pointer',
                          }}
                          onFocus={e => e.target.style.borderColor = TEAL}
                          onBlur={e => e.target.style.borderColor = '#e9ecef'}>
                          <option value="">Select a subject</option>
                          <option value="General Enquiry">General Enquiry</option>
                          <option value="Find Parking">Find Parking</option>
                          <option value="List My Lot">List My Lot</option>
                          <option value="Technical Support">Technical Support</option>
                          <option value="Partnership">Partnership</option>
                        </select>
                      </div>

                      <div className="col-12">
                        <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                          Message *
                        </label>
                        <textarea name="message" required rows={5}
                          value={form.message} onChange={handleChange}
                          placeholder="Tell us how we can help you..."
                          style={{
                            width: '100%', padding: '12px 16px',
                            border: '1.5px solid #e9ecef', borderRadius: '10px',
                            fontSize: '14px', color: DARK, outline: 'none',
                            background: WHITE, resize: 'vertical',
                            transition: 'border-color 0.2s', lineHeight: 1.7,
                          }}
                          onFocus={e => e.target.style.borderColor = TEAL}
                          onBlur={e => e.target.style.borderColor = '#e9ecef'} />
                      </div>

                      {error && (
                        <div className="col-12">
                          <div style={{
                            background: '#FEE2E2', border: '1px solid #FECACA',
                            borderRadius: '10px', padding: '12px 16px',
                            color: '#DC2626', fontSize: '13px', fontWeight: '600',
                          }}>
                            {error}
                          </div>
                        </div>
                      )}

                      <div className="col-12">
                        <button type="submit" disabled={loading}
                          style={{
                            background: loading ? '#B2DFDB' : TEAL,
                            color: WHITE, border: 'none', borderRadius: '10px',
                            padding: '14px 40px', fontWeight: '700', fontSize: '15px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#009966'; }}
                          onMouseLeave={e => { if (!loading) e.currentTarget.style.background = TEAL; }}>
                          {loading ? 'Sending...' : 'Send Message'}
                          {!loading && <Send size={17} />}
                        </button>
                      </div>

                    </div>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="cta-outer" style={{ background: '#F5F5F5', padding: '40px 24px 100px' }}>
        <div className="cta-inner" style={{
          background: TEAL, borderRadius: '20px',
          padding: '60px 60px 0', overflow: 'visible', position: 'relative',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '24px 24px', borderRadius: '20px', pointerEvents: 'none',
          }} />
          <div className="row align-items-end" style={{ position: 'relative', zIndex: 1 }}>
            <div className="col-lg-7" style={{ paddingBottom: '60px' }}>
              <h2 style={{ fontWeight: '900', fontSize: '2.8rem', color: WHITE, lineHeight: 1.15, letterSpacing: '-1px', marginBottom: '16px' }}>
                Let's find your perfect<br />parking spot today!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', marginBottom: '36px', lineHeight: 1.7, maxWidth: '480px' }}>
                Park smarter with real-time availability, M-Pesa payments, and instant reservations — anytime, anywhere.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <a href="https://play.google.com/store/apps/details?id=com.spotfinder.owner&hl=en"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on Google Play"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Play size={22} fill={WHITE} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>GET IT ON</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>Google Play</div>
                  </div>
                </a>
                <a href="https://apps.apple.com/ke/app/spotfinder-parking-finder/id6572302355"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on App Store"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Smartphone size={22} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Download on the</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>App Store</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-flex justify-content-center align-items-end">
              <img
                className="cta-phone"
                src="/images/spotfinder-screen.webp"
                alt="Spot Finder app screen"
                style={{
                  width: '480px', display: 'block',
                  marginBottom: '-80px', marginTop: '-40px',
                  filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.35))',
                  position: 'relative', zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}